import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import CurrencyInput from '../common/CurrencyInput';
import { formatCurrency } from '../../utils/formatters';
import { calculateAgentSplit } from '../../utils/calculations';
import { useCalculator } from '../../contexts/CalculatorContext';

interface AgentCommissionSplitProps {
  entity: string;
}

const AgentCommissionSplit: React.FC<AgentCommissionSplitProps> = ({ entity }) => {
  const { vatRate } = useCalculator();
  const [propertyPrice, setPropertyPrice] = useState<number>(0);
  const [estateAgencyCommission, setEstateAgencyCommission] = useState<number>(10);
  const [agentCommission, setAgentCommission] = useState<number>(10);
  const [result, setResult] = useState<{
    totalCommissionToAgency: number;
    vatOnCommission: number;
    commissionToAgent: number;
    commissionToAgency: number;
    nettToClient: number;
  } | null>(null);

  useEffect(() => {
    if (
      propertyPrice > 0 &&
      estateAgencyCommission > 0 &&
      estateAgencyCommission < 99 &&
      agentCommission > 0 &&
      agentCommission < 99
    ) {
      const calc = calculateAgentSplit(propertyPrice, estateAgencyCommission, agentCommission, vatRate);
      setResult(calc);
    } else {
      setResult(null);
    }
  }, [propertyPrice, estateAgencyCommission, agentCommission, vatRate]);

  const agentLabel = entity === 'Individual' ? 'Property Practitioner' : 'Estate Agent';

  return (
    <div className="p-2">
      <CurrencyInput value={propertyPrice} onChange={setPropertyPrice} label="Property Price" required />

      <Form.Group className="mb-2">
        <div className="form-group has-float-label">
          <Form.Control
            type="number"
            min="0.1"
            max="99"
            step="0.1"
            value={estateAgencyCommission}
            onChange={(e) => setEstateAgencyCommission(parseFloat(e.target.value) || 10)}
            required
            placeholder=" "
          />
          <span className="required">Professional Fee Estate Agency (%)</span>
        </div>
      </Form.Group>

      <Form.Group className="mb-2">
        <div className="form-group has-float-label">
          <Form.Control
            type="number"
            min="0.1"
            max="99"
            step="0.1"
            value={agentCommission}
            onChange={(e) => setAgentCommission(parseFloat(e.target.value) || 10)}
            required
            placeholder=" "
          />
          <span className="required">Professional Fee {agentLabel} (%)</span>
        </div>
      </Form.Group>

      {result && (
        <table className="table table-sm mt-2" style={{ fontSize: '13px' }}>
          <thead>
            <tr>
              <th colSpan={2} className="text-secondary border-0 py-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                CALCULATED
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border-0 py-1">Total Professional Fee Estate Agency</td>
              <td className="text-end border-0 font-weight-bold py-1">R {formatCurrency(result.totalCommissionToAgency)}</td>
            </tr>
            <tr>
              <td className="py-1">Vat on Professional Fee</td>
              <td className="text-end font-weight-bold py-1">R {formatCurrency(result.vatOnCommission)}</td>
            </tr>
            <tr>
              <td className="py-1">Professional Fee {agentLabel}</td>
              <td className="text-end font-weight-bold py-1">R {formatCurrency(result.commissionToAgent)}</td>
            </tr>
            <tr>
              <td className="py-1">Professional Fee to Estate Agency</td>
              <td className="text-end font-weight-bold py-1">R {formatCurrency(result.commissionToAgency)}</td>
            </tr>
            <tr>
              <td className="py-1">Nett to Client (Owner)</td>
              <td className="text-end font-weight-bold py-1">R {formatCurrency(result.nettToClient)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AgentCommissionSplit;
